import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, LockSimple, Paperclip } from "@phosphor-icons/react";
import { isAdmin, isParent, can } from "@/lib/roles";
import { AllergyPanel } from "@/pages/admin/student/AllergyPanel";
import { MedicationsPanel } from "@/pages/admin/student/MedicationsPanel";

const FIELDS = [["medications", "Medications"], ["dietary_restrictions", "Dietary restrictions"],
  ["medical_conditions", "Medical conditions"], ["emergency_instructions", "Emergency instructions"], ["immunization_status", "Immunization status"],
  ["care_plan", "Individual care plan"], ["accessibility_needs", "Accessibility needs"], ["classroom_accommodations", "Special classroom accommodations"],
  ["physician", "Physician"], ["physician_phone", "Physician phone"], ["insurance", "Insurance"], ["restricted_notes", "Restricted administrator notes"]];
const ALL_FIELDS = ["allergy_status", "allergy_entries", "allergy_review_date", "allergy_verified_by", "allergies",
  ...FIELDS.map(([k]) => k)];
const APPLICABLE = ["medications", "dietary_restrictions", "care_plan", "emergency_instructions",
  "accessibility_needs", "classroom_accommodations", "physician", "physician_phone"];
const blankSvc = { type: "Speech therapy", status: "Active", start_date: "", end_date: "", provider: "", schedule: "", accommodations: "",
  classroom_instructions: "", parent_authorization: "Pending", consent_expires: "", review_date: "", restricted_notes: "", reason: "" };

export const HealthTab = ({ student, user }) => {
  const [rec, setRec] = useState(null);
  const [svcs, setSvcs] = useState([]);
  const [meta, setMeta] = useState({ types: [], statuses: [] });
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [svc, setSvc] = useState(null);
  const [review, setReview] = useState(null);
  const canEdit = can(user, "health", "edit") && !isParent(user);

  const load = useCallback(async () => {
    try {
      const [h, s, m] = await Promise.all([api.get(`/students/${student.id}/health`), api.get(`/students/${student.id}/services`), api.get("/services/meta")]);
      setRec(h.data); setSvcs(s.data); setMeta(m.data); setForm({ ...h.data, reason: "" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [student.id]);
  useEffect(() => { load(); }, [load]);

  /** The health endpoint replaces the whole record, so every save sends the full current record. */
  const saveRecord = async (partial, reason) => {
    const base = Object.fromEntries(ALL_FIELDS.map((k) => [k, rec?.[k] ?? (k === "allergy_entries" ? [] : "")]));
    await api.put(`/students/${student.id}/health`, { ...base, ...partial, reason: reason || "" });
    await load();
  };

  const save = async () => {
    try {
      await saveRecord(Object.fromEntries(FIELDS.map(([k]) => [k, form[k] || ""])), form.reason);
      toast.success("Health record saved"); setEdit(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveSvc = async () => {
    try {
      const payload = { ...svc }; const id = payload.id;
      ["id", "student_id", "student_name", "documents", "created_by", "created_at", "updated_by", "updated_at", "limited"].forEach((k) => delete payload[k]);
      if (id) await api.put(`/services/${id}`, payload); else await api.post(`/students/${student.id}/services`, payload);
      toast.success("Service saved"); setSvc(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const archive = async (id) => {
    const reason = window.prompt("Reason for archiving this service?"); if (reason === null) return;
    try { await api.delete(`/services/${id}`, { params: { reason } }); toast.success("Service archived"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const uploadDoc = async (id, file) => {
    if (!file) return; const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/services/${id}/document`, fd); toast.success("Document uploaded"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const decide = async (decision) => {
    try {
      await api.post(`/health/requests/${review.id}/review`, {
        decision, note: review.note || "",
        apply_field: decision === "approved" && review.apply_field ? review.apply_field : "",
        apply_value: review.apply_value || "" });
      toast.success(decision === "approved" ? "Request approved" : "Request declined");
      setReview(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!rec) return <p className="text-muted-foreground">Loading…</p>;
  const shown = FIELDS.filter(([k]) => k in rec || canEdit);
  const pending = (rec.pending_requests || []).filter((r) => r.status === "pending");

  return (
    <div className="space-y-4" data-testid="health-tab">
      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-xs font-semibold text-amber-800 flex items-center gap-2" data-testid="health-restricted-notice">
        <LockSimple size={14} /> Restricted record. {rec.limited ? "You are seeing only what is needed to care for this child safely." : "Every view and change here is written to the access log."}
      </div>

      <AllergyPanel rec={rec} student={student} user={user} onSave={saveRecord} onChanged={load} />

      {pending.length > 0 && canEdit && (
        <div className="bg-white rounded-2xl border border-border p-6" data-testid="health-requests-panel">
          <h2 className="font-display text-lg font-semibold">Family corrections awaiting review</h2>
          <div className="mt-3 space-y-3">
            {pending.map((r) => (
              <div key={r.id} className="rounded-2xl border border-border p-4" data-testid={`health-request-${r.id}`}>
                <p className="font-bold text-sm">{r.area_label} · {r.submitted_by_name} ({r.relationship})</p>
                <p className="text-sm mt-1 whitespace-pre-wrap">{r.details}</p>
                {r.requested_change && <p className="text-sm mt-1 text-muted-foreground whitespace-pre-wrap">Requested wording: {r.requested_change}</p>}
                <Button size="sm" className="mt-3 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                        onClick={() => setReview({ ...r, note: "", apply_field: "", apply_value: r.requested_change || "" })}
                        data-testid={`review-request-${r.id}`}>Review</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-border p-6" data-testid="health-record">
        <div className="flex justify-between items-start gap-3">
          <h2 className="font-display text-lg font-semibold">Health record</h2>
          {canEdit && !edit && <Button size="sm" variant="outline" className="rounded-full font-bold" onClick={() => setEdit(true)} data-testid="edit-health-btn">Edit</Button>}
        </div>
        {!edit ? (
          <dl className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
            {shown.map(([k, label]) => (
              <div key={k} data-testid={`health-${k}`}><dt className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{label}</dt><dd className="mt-0.5 font-semibold whitespace-pre-wrap">{rec[k] || <span className="text-muted-foreground">None recorded</span>}</dd></div>
            ))}
            <div className="sm:col-span-2"><dt className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Immunizations on file</dt>
              <dd className="mt-1 flex flex-wrap gap-2">{(rec.immunizations || []).length === 0 && <span className="text-sm text-muted-foreground">None recorded — add them from the Forms &amp; Documents tab.</span>}
                {(rec.immunizations || []).map((im) => <span key={im.id} className={`text-xs font-bold px-3 py-1 rounded-full ${im.next_due && im.next_due < new Date().toISOString().slice(0, 10) ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`}>{im.vaccine} · {im.date_given || "—"}</span>)}</dd></div>
          </dl>
        ) : (
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            {FIELDS.map(([k, label]) => (
              <div key={k} className={k === "restricted_notes" || k === "emergency_instructions" || k === "care_plan" ? "sm:col-span-2" : ""}>
                <Label className="text-sm font-bold">{label}</Label>
                {["emergency_instructions", "care_plan", "restricted_notes", "classroom_accommodations"].includes(k)
                  ? <Textarea value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-2 rounded-xl" data-testid={`health-field-${k}`} />
                  : <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-2 h-11 rounded-xl" data-testid={`health-field-${k}`} />}
              </div>
            ))}
            <div className="sm:col-span-2"><Label className="text-sm font-bold">Reason for change</Label><Input value={form.reason || ""} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-2 h-11 rounded-xl" data-testid="health-reason" /></div>
            <div className="sm:col-span-2 flex gap-2"><Button onClick={save} className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="save-health-btn">Save health record</Button>
              <Button variant="ghost" className="rounded-full" onClick={() => setEdit(false)}>Cancel</Button></div>
          </div>
        )}
      </div>

      {can(user, "health", "view") && <MedicationsPanel student={student} user={user} />}

      <div className="bg-white rounded-2xl border border-border p-6" data-testid="services-panel">
        <div className="flex justify-between items-start gap-3">
          <div><h2 className="font-display text-lg font-semibold">Therapy &amp; support services</h2>
            <p className="text-xs text-muted-foreground mt-1">{svcs.length ? `${svcs.filter((s) => s.status === "Active").length} active` : "No services on file"}</p></div>
          {canEdit && <Button size="sm" className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" onClick={() => setSvc(blankSvc)} data-testid="add-service-btn"><Plus size={15} className="mr-1" /> Add service</Button>}
        </div>
        <div className="mt-4 space-y-3">
          {svcs.map((s) => (
            <div key={s.id} className="rounded-2xl border border-border p-4" data-testid={`service-${s.id}`}>
              <div className="flex flex-wrap gap-2 items-center">
                <p className="font-bold">{s.type}</p>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${s.status === "Active" ? "bg-emerald-50 text-emerald-700" : "bg-muted"}`}>{s.status}</span>
                {s.parent_authorization && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700">Consent: {s.parent_authorization}</span>}
                {canEdit && <span className="ml-auto flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-full font-bold" onClick={() => setSvc({ ...blankSvc, ...s, reason: "" })} data-testid={`edit-service-${s.id}`}>Edit</Button>
                  {isAdmin(user) && <Button size="sm" variant="ghost" className="rounded-full text-[#EF4444]" onClick={() => archive(s.id)} data-testid={`archive-service-${s.id}`}>Archive</Button>}</span>}
              </div>
              <dl className="mt-3 grid sm:grid-cols-2 gap-2 text-sm">
                {[["Schedule", s.schedule], ["Provider", s.provider], ["Start", s.start_date], ["End", s.end_date], ["Classroom instructions", s.classroom_instructions],
                  ["Accommodations", s.accommodations], ["Consent expires", s.consent_expires], ["Review date", s.review_date], ["Restricted notes", s.restricted_notes]]
                  .filter(([, v]) => v !== undefined && v !== "").map(([k, v]) => <div key={k}><dt className="text-xs text-muted-foreground font-semibold">{k}</dt><dd className="font-semibold">{v}</dd></div>)}
              </dl>
              {s.documents && (
                <div className="mt-3 flex flex-wrap gap-2 items-center text-xs">
                  {s.documents.map((d) => <span key={d.path} className="px-2.5 py-1 rounded-full bg-muted font-semibold">{d.name}</span>)}
                  {canEdit && <label className="cursor-pointer inline-flex items-center gap-1 px-3 py-1 rounded-full border border-border font-bold"><Paperclip size={13} /> Upload<input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => uploadDoc(s.id, e.target.files?.[0])} data-testid={`service-doc-${s.id}`} /></label>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {review && (
        <Dialog open onOpenChange={() => setReview(null)}>
          <DialogContent className="rounded-3xl max-w-lg" data-testid="review-request-dialog">
            <DialogHeader><DialogTitle className="font-display">Review family correction</DialogTitle>
              <DialogDescription>Approving records the decision. Nothing on the record changes unless you choose a field to apply.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <p className="text-sm font-semibold whitespace-pre-wrap">{review.details}</p>
              <div><Label className="text-sm font-bold">Apply to field (optional)</Label>
                <Select value={review.apply_field || "none"} onValueChange={(v) => setReview({ ...review, apply_field: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="review-apply-field"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Do not change the record</SelectItem>
                    {APPLICABLE.map((f) => <SelectItem key={f} value={f}>{f.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select></div>
              {review.apply_field && (
                <div><Label className="text-sm font-bold">New value</Label>
                  <Textarea value={review.apply_value || ""} onChange={(e) => setReview({ ...review, apply_value: e.target.value })}
                            className="mt-1 rounded-xl" data-testid="review-apply-value" /></div>
              )}
              <div><Label className="text-sm font-bold">Note to file</Label>
                <Input value={review.note || ""} onChange={(e) => setReview({ ...review, note: e.target.value })}
                       className="mt-1 h-11 rounded-xl" data-testid="review-note" /></div>
              <div className="flex gap-2">
                <Button onClick={() => decide("approved")} className="flex-1 h-11 rounded-full bg-[#10B981] hover:bg-emerald-600 font-bold"
                        data-testid="approve-request-btn">Approve</Button>
                <Button onClick={() => decide("declined")} variant="outline" className="h-11 rounded-full font-bold text-[#EF4444]"
                        data-testid="decline-request-btn">Decline</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {svc && (
        <Dialog open onOpenChange={() => setSvc(null)}>
          <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="service-dialog">
            <DialogHeader><DialogTitle className="font-display">{svc.id ? "Edit service" : "Add support service"}</DialogTitle><DialogDescription>Restricted to authorized administrators. Recorded in the access log.</DialogDescription></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-sm font-bold">Type</Label><Select value={svc.type} onValueChange={(v) => setSvc({ ...svc, type: v })}><SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="service-type"><SelectValue /></SelectTrigger><SelectContent>{meta.types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-sm font-bold">Status</Label><Select value={svc.status} onValueChange={(v) => setSvc({ ...svc, status: v })}><SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="service-status"><SelectValue /></SelectTrigger><SelectContent>{meta.statuses.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-sm font-bold">Parent authorization</Label><Select value={svc.parent_authorization} onValueChange={(v) => setSvc({ ...svc, parent_authorization: v })}><SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="service-auth"><SelectValue /></SelectTrigger><SelectContent>{["Authorized", "Pending", "Declined"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              {[["provider", "Provider / agency"], ["schedule", "Service schedule"], ["start_date", "Start date", "date"], ["end_date", "End date", "date"], ["consent_expires", "Consent expires", "date"], ["review_date", "Review date", "date"]].map(([k, label, type]) => (
                <div key={k}><Label className="text-sm font-bold">{label}</Label><Input type={type || "text"} value={svc[k] || ""} onChange={(e) => setSvc({ ...svc, [k]: e.target.value })} className="mt-2 h-11 rounded-xl" data-testid={`service-${k}`} /></div>))}
              {[["accommodations", "Accommodations"], ["classroom_instructions", "Classroom instructions"], ["restricted_notes", "Restricted administrator notes"], ["reason", "Reason for change"]].map(([k, label]) => (
                <div key={k} className="sm:col-span-2"><Label className="text-sm font-bold">{label}</Label><Textarea value={svc[k] || ""} onChange={(e) => setSvc({ ...svc, [k]: e.target.value })} className="mt-2 rounded-xl" data-testid={`service-${k}`} /></div>))}
            </div>
            <Button onClick={saveSvc} className="w-full h-11 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="save-service-btn">Save service</Button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
